import config from '../config.json'

const SYNC_DEBOUNCE_MS = 2000

let cachedVapidPublicKey = null
let lastSyncedSubscriptionEndpoint = null
let lastSyncedToken = null
let registerInFlight = null
let syncDebounceTimer = null

export function isPushNotificationsEnabled() {
  return config.pushNotificationsEnabled === true
}

function isPushRuntimeEnabled() {
  if (import.meta.env.DEV) return false
  return isPushNotificationsEnabled()
}

function getPushApiUrl() {
  if (!config.apiUrl) {
    throw new Error('apiUrl no configurada')
  }
  return config.apiUrl
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)

  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i)
  }

  return outputArray
}

function clearSubscriptionSyncCache() {
  lastSyncedSubscriptionEndpoint = null
  lastSyncedToken = null
}

function subscriptionNeedsServerSync(subscription) {
  if (!subscription?.endpoint) return false
  if (subscription.endpoint !== lastSyncedSubscriptionEndpoint) return true
  if (config.token !== lastSyncedToken) return true
  return false
}

function markSubscriptionSynced(subscription) {
  lastSyncedSubscriptionEndpoint = subscription?.endpoint ?? null
  lastSyncedToken = config.token ?? null
}

async function fetchVapidPublicKey() {
  if (cachedVapidPublicKey) return cachedVapidPublicKey

  const response = await fetch(`${getPushApiUrl()}/push/vapid-public-key`)

  if (!response.ok) {
    throw new Error('No se pudo obtener la clave VAPID')
  }

  const data = await response.json()

  if (data.status !== 'ok' || !data.body?.publicKey) {
    throw new Error(data.msg || 'Respuesta VAPID invalida')
  }

  cachedVapidPublicKey = data.body.publicKey
  return cachedVapidPublicKey
}

async function getServiceWorkerRegistration() {
  if (!('serviceWorker' in navigator)) return null

  await navigator.serviceWorker.register('/sw.js')
  return navigator.serviceWorker.ready
}

async function getCurrentSubscription(registration) {
  if (!registration?.pushManager) return null
  return registration.pushManager.getSubscription()
}

async function registerSubscriptionOnServer(subscription) {
  const response = await fetch(`${getPushApiUrl()}/push/subscribe`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      token: config.token,
    },
    body: JSON.stringify({
      subscription: subscription.toJSON(),
      userAgent: navigator.userAgent,
    }),
  })

  const data = await response.json()

  if (data.status !== 'ok') {
    throw new Error(data.msg || 'No se pudo registrar la suscripcion push')
  }
}

async function ensurePushSubscription(registration) {
  const publicKey = await fetchVapidPublicKey()
  const options = {
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey),
  }

  const existing = await registration.pushManager.getSubscription()
  if (existing) {
    try {
      return await registration.pushManager.subscribe(options)
    } catch {
      await existing.unsubscribe()
    }
  }

  return registration.pushManager.subscribe(options)
}

async function runPushNotificationRegistration() {
  if (Notification.permission === 'default') {
    const permission = await Notification.requestPermission()
    if (permission !== 'granted') return
  } else if (Notification.permission !== 'granted') {
    return
  }

  const registration = await getServiceWorkerRegistration()
  if (!registration?.pushManager) return

  const existing = await getCurrentSubscription(registration)
  if (existing && !subscriptionNeedsServerSync(existing)) return

  const subscription = await ensurePushSubscription(registration)
  if (!subscriptionNeedsServerSync(subscription)) return

  await registerSubscriptionOnServer(subscription)
  markSubscriptionSynced(subscription)
}

export async function registerPushNotifications() {
  if (!isPushRuntimeEnabled()) return
  if (!config.token) return
  if (!config.apiUrl) return
  if (!('Notification' in window) || !('PushManager' in window)) return

  if (registerInFlight) return registerInFlight

  registerInFlight = runPushNotificationRegistration()
    .catch((error) => {
      console.error('Push notifications registration failed:', error)
    })
    .finally(() => {
      registerInFlight = null
    })

  return registerInFlight
}

function schedulePushNotificationSync() {
  clearTimeout(syncDebounceTimer)
  syncDebounceTimer = setTimeout(() => {
    registerPushNotifications()
  }, SYNC_DEBOUNCE_MS)
}

export function setupPushNotificationSync() {
  if (!isPushRuntimeEnabled()) return
  if (!('serviceWorker' in navigator)) return

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      schedulePushNotificationSync()
    }
  })

  window.addEventListener('focus', () => {
    schedulePushNotificationSync()
  })

  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data?.type === 'PUSH_RESUBSCRIBE') {
      clearSubscriptionSyncCache()
      schedulePushNotificationSync()
    }
  })
}

export async function unregisterPushNotifications() {
  if (!isPushRuntimeEnabled()) return
  if (!config.token) return
  if (!config.apiUrl) return
  if (!('serviceWorker' in navigator)) return

  try {
    const registration = await getServiceWorkerRegistration()
    const subscription = await getCurrentSubscription(registration)

    if (!subscription) return

    await fetch(`${getPushApiUrl()}/push/unsubscribe`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        token: config.token,
      },
      body: JSON.stringify({
        subscription: subscription.toJSON(),
      }),
    })

    await subscription.unsubscribe()
    clearSubscriptionSyncCache()
  } catch (error) {
    console.warn('Push notifications unregister skipped:', error.message)
  }
}
